import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import AdminShell from "@/components/admin/AdminShell";

const ADMIN_CTX_KEY = "ybm_admin_ctx";

/**
 * SmartShell — automatically renders AdminShell for owners browsing customer
 * tools FROM the admin sidebar (so the founder never loses the admin chrome
 * while testing BYOK flows). Falls back to DashboardShell for everyone else.
 *
 * Detection rules:
 *   1. user.role === "owner" (non-owners are always customers)
 *   2. AND either ?admin=1 in the URL OR sessionStorage flag set.
 *
 * Once admin context is detected, sessionStorage persists it across in-page
 * navigation so deep-linked Reuse-brief / Smart Export flows don't bounce the
 * owner back to DashboardShell. The "Exit to Customer App" link in AdminShell
 * clears the flag on click.
 */
export default function SmartShell({ children, ...adminProps }) {
  const { user } = useAuth();
  const location = useLocation();
  const isOwner = user?.role === "owner";

  const params = new URLSearchParams(location.search);
  const adminParam = params.get("admin") === "1";
  const stored = typeof window !== "undefined" && window.sessionStorage.getItem(ADMIN_CTX_KEY) === "1";

  const inAdminContext = isOwner && (adminParam || stored);

  useEffect(() => {
    if (!isOwner) return;
    if (adminParam) {
      window.sessionStorage.setItem(ADMIN_CTX_KEY, "1");
    }
  }, [adminParam, isOwner]);

  if (inAdminContext) {
    return <AdminShell {...adminProps}>{children}</AdminShell>;
  }
  return <DashboardShell>{children}</DashboardShell>;
}

export { ADMIN_CTX_KEY };
